import React, { useEffect, useRef } from 'react'
import * as echarts from 'echarts'
import styled from 'styled-components'
import { Loading } from '../../atoms/Loading'
import { Flex } from '../../atoms/Flex'
import { PriceDistributionChartProps } from './PriceDistributionChart.types'

const ChartContainer = styled.div`
  width: 100%;
  height: 100%;
  min-height: 350px;
  position: relative;
`

const ChartWrapper = styled.div<{ $height: number }>`
  width: 100%;
  height: ${({ $height }) => $height}px;
`

const ErrorContainer = styled(Flex)`
  height: 350px;
  color: ${({ theme }) => theme.colors.text.secondary};
`

const LoadingContainer = styled(Flex)`
  height: 350px;
`

export const PriceDistributionChart: React.FC<PriceDistributionChartProps> = ({
  data,
  loading = false,
  error = null,
  title = 'Price Distribution',
  height = 400,
  formatCurrency = (value: number) => {
    if (value >= 1e9) return `CHF ${(value / 1e9).toFixed(1)}B`
    if (value >= 1e6) return `CHF ${(value / 1e6).toFixed(1)}M`
    if (value >= 1e3) return `CHF ${(value / 1e3).toFixed(0)}K`
    return `CHF ${value.toFixed(0)}`
  },
}) => {
  const chartRef = useRef<HTMLDivElement>(null)
  const chartInstance = useRef<echarts.ECharts | null>(null)

  useEffect(() => {
    if (!loading && !error && data && data.length > 0 && chartRef.current) {
      initializeChart()
    }

    return () => {
      chartInstance.current?.dispose()
    }
  }, [data, loading, error])

  useEffect(() => {
    const handleResize = () => {
      chartInstance.current?.resize()
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const initializeChart = () => {
    if (!chartRef.current || !data) return

    if (chartInstance.current) {
      chartInstance.current.dispose()
    }

    chartInstance.current = echarts.init(chartRef.current)

    const option: echarts.EChartsOption = {
      title: {
        text: title,
        left: 'center',
        top: 10,
        textStyle: {
          fontSize: 16,
          fontWeight: 600,
        },
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        formatter: (params: any) => {
          const param = params[0]
          return `
            <div style="padding: 8px;">
              <div style="font-weight: 600; margin-bottom: 4px;">Price Range: ${param.name}</div>
              <div>Number of Transactions: ${param.value.toLocaleString()}</div>
            </div>
          `
        },
      },
      grid: {
        left: '10%',
        right: '10%',
        bottom: '15%',
        top: '15%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: data.map((d) => {
          const range = d.range
          if (range.includes('-')) {
            const [from, to] = range.split('-').map((s) => s.trim())
            const fromNum = parseInt(from) || 0
            const toNum = to === '+' ? null : parseInt(to)
            if (toNum) {
              return `${formatCurrency(fromNum)} - ${formatCurrency(toNum)}`
            } else {
              return `> ${formatCurrency(fromNum)}`
            }
          }
          return range
        }),
        axisLabel: {
          rotate: 45,
          fontSize: 11,
        },
      },
      yAxis: {
        type: 'value',
        name: 'Number of Transactions',
        nameLocation: 'middle',
        nameGap: 50,
        axisLabel: {
          formatter: (value: number) => value.toLocaleString(),
        },
      },
      series: [
        {
          type: 'bar',
          data: data.map((d) => d.count),
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: '#3b82f6' },
              { offset: 1, color: '#1d4ed8' },
            ]),
            borderRadius: [4, 4, 0, 0],
          },
          emphasis: {
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#60a5fa' },
                { offset: 1, color: '#2563eb' },
              ]),
            },
          },
        },
      ],
    }

    chartInstance.current.setOption(option)
  }

  if (loading) {
    return (
      <LoadingContainer justify="center" align="center">
        <Loading size="large" />
      </LoadingContainer>
    )
  }

  if (error) {
    return (
      <ErrorContainer justify="center" align="center">
        {error}
      </ErrorContainer>
    )
  }

  if (!data || data.length === 0) {
    return (
      <ErrorContainer justify="center" align="center">
        No price distribution data available
      </ErrorContainer>
    )
  }

  return (
    <ChartContainer>
      <ChartWrapper ref={chartRef} $height={height} />
    </ChartContainer>
  )
}
